<script>
import { formatMinskTimestamp } from '@/scripts/tools.js';
import { generateCorporateRequestEmail } from '@/scripts/emailTemplates.js';
import { getStatusConfig } from '@/scripts/corporateRequests.js';
import constants from '@/constants';

export default {
    data() {
        return {
            localAdminNotes: '',
            notesCharCount: 0,
            touched: {
                adminNotes: false,
            },
        };
    },
    computed: {
        constants() {
            return constants;
        },
        isShow() {
            return this.$store.getters.IS_SHOW_REQUEST_DETAIL;
        },
        request() {
            return this.$store.getters.REQUEST_DETAIL_DATA;
        },
        isLoading() {
            return this.$store.getters.IS_DETAIL_LOADING;
        },
    },
    watch: {
        request: {
            handler(newRequest) {
                if (!newRequest) {
                    this.resetNotes();
                    return;
                }
                this.localAdminNotes = newRequest.admin_notes || '';
                this.notesCharCount = this.localAdminNotes.length;
            },
            immediate: true,
        },
    },
    methods: {
        close() {
            this.$store.commit('IS_SHOW_REQUEST_DETAIL', false);
            this.$store.commit('REQUEST_DETAIL_SET', null);
            this.resetNotes();
        },
        resetNotes() {
            this.localAdminNotes = '';
            this.notesCharCount = 0;
            this.touched.adminNotes = false;
        },
        updateNotesCount() {
            this.notesCharCount = this.localAdminNotes.length;
        },
        showPopup(title, description, isSuccess = true) {
            this.$store.commit('POPUP_ADD', {
                title,
                description: [description],
                color: isSuccess ? constants.COLOR.GREEN : constants.COLOR.RED,
            });
        },
        async changeStatus(newStatus) {
            if (!this.request) return;

            try {
                await this.$store.dispatch('updateRequestStatus', {
                    requestId: this.request.id,
                    status: newStatus,
                    admin_notes: this.localAdminNotes || null,
                });
                this.showPopup(
                    'STATUS UPDATED',
                    `Request status changed to ${newStatus}`,
                    true
                );
            } catch (error) {
                console.error('UPDATE FAILED:', error);
                this.showPopup(
                    'UPDATE FAILED',
                    'Failed to update request status',
                    false
                );
            }
        },
        async saveNotes() {
            if (!this.request) return;

            try {
                await this.$store.dispatch('updateRequestStatus', {
                    requestId: this.request.id,
                    status: this.request.status,
                    admin_notes: this.localAdminNotes || null,
                });
                this.showPopup(
                    'NOTES SAVED',
                    'Admin notes updated successfully',
                    true
                );
                this.touched.adminNotes = false;
            } catch (error) {
                console.error('SAVE FAILED:', error);
                this.showPopup(
                    'SAVE FAILED',
                    'Failed to save admin notes',
                    false
                );
            }
        },
        formatDate(isoString) {
            return formatMinskTimestamp(isoString);
        },
        openEmail() {
            if (!this.request) return;

            const { subject, body } = generateCorporateRequestEmail(
                this.request
            );
            window.location.href = `mailto:${
                this.request.email
            }?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
                body
            )}`;
        },
        viewUserProfile() {
            if (!this.request) return;
            this.$router.push({
                name: constants.ROUTER_NAME.USEREDIT,
                params: { userId: this.request.user_id },
            });
        },
    },
};
</script>

<template>
    <div v-if="isShow" class="modal_overlay" @click.self="close">
        <div class="modal_content">
            <div class="modal_header">
                <h2>Corporate Request Details</h2>
                <button @click="close" class="close_button">✕</button>
            </div>

            <div v-if="isLoading" class="loading_state">
                <p>Loading request details...</p>
            </div>

            <div v-else-if="request" class="modal_body">
                <!-- Status Section -->
                <section class="detail_section">
                    <h3>Status Management</h3>
                    <div class="status_controls">
                        <label>Current Status:</label>
                        <div class="status_buttons">
                            <button
                                @click="
                                    changeStatus(
                                        constants.CORPORATE_REQUEST_STATUS
                                            .PENDING
                                    )
                                "
                                :class="{
                                    active:
                                        request.status ===
                                        constants.CORPORATE_REQUEST_STATUS
                                            .PENDING,
                                }"
                                class="status_btn status_pending"
                            >
                                Pending
                            </button>
                            <button
                                @click="
                                    changeStatus(
                                        constants.CORPORATE_REQUEST_STATUS
                                            .CONTACTED
                                    )
                                "
                                :class="{
                                    active:
                                        request.status ===
                                        constants.CORPORATE_REQUEST_STATUS
                                            .CONTACTED,
                                }"
                                class="status_btn status_contacted"
                            >
                                Contacted
                            </button>
                            <button
                                @click="
                                    changeStatus(
                                        constants.CORPORATE_REQUEST_STATUS
                                            .CONVERTED
                                    )
                                "
                                :class="{
                                    active:
                                        request.status ===
                                        constants.CORPORATE_REQUEST_STATUS
                                            .CONVERTED,
                                }"
                                class="status_btn status_converted"
                            >
                                Converted
                            </button>
                            <button
                                @click="
                                    changeStatus(
                                        constants.CORPORATE_REQUEST_STATUS
                                            .REJECTED
                                    )
                                "
                                :class="{
                                    active:
                                        request.status ===
                                        constants.CORPORATE_REQUEST_STATUS
                                            .REJECTED,
                                }"
                                class="status_btn status_rejected"
                            >
                                Rejected
                            </button>
                        </div>
                    </div>
                </section>

                <hr class="section_divider" />

                <!-- Company Information -->
                <section class="detail_section">
                    <h3>Company Information</h3>
                    <div class="info_grid">
                        <div class="info_item">
                            <label>Company Name:</label>
                            <span>{{ request.company_name }}</span>
                        </div>
                        <div class="info_item">
                            <label>Contact Person:</label>
                            <span>{{ request.contact_name }}</span>
                        </div>
                        <div class="info_item">
                            <label>Email:</label>
                            <span class="email_value">
                                {{ request.email }}
                                <button @click="openEmail" class="email_btn">
                                    📧
                                </button>
                            </span>
                        </div>
                        <div class="info_item">
                            <label>Licenses Requested:</label>
                            <span class="licenses_value">{{
                                request.licenses_count
                            }}</span>
                        </div>
                    </div>
                </section>

                <hr class="section_divider" />

                <!-- User Message -->
                <section class="detail_section">
                    <h3>User Message</h3>
                    <div
                        v-if="request.message && request.message.trim()"
                        class="message_box"
                    >
                        <p>{{ request.message }}</p>
                    </div>
                    <div v-else class="no_message">
                        <p>No additional message provided</p>
                    </div>
                </section>

                <hr class="section_divider" />

                <!-- Admin Notes -->
                <section class="detail_section">
                    <h3>
                        Admin Notes (Internal)
                        <span
                            class="char_counter"
                            :class="{ warning: notesCharCount > 1800 }"
                        >
                            {{ notesCharCount }}/2000
                        </span>
                    </h3>
                    <textarea
                        v-model="localAdminNotes"
                        @input="
                            updateNotesCount();
                            touched.adminNotes = true;
                        "
                        @blur="touched.adminNotes = true"
                        maxlength="2000"
                        placeholder="Add internal notes about this request..."
                        class="notes_textarea"
                        :class="{ warning: notesCharCount > 1800 }"
                    ></textarea>
                    <button
                        @click="saveNotes"
                        :disabled="!touched.adminNotes || notesCharCount > 2000"
                        class="save_notes_btn"
                    >
                        Save Notes
                    </button>
                </section>

                <hr class="section_divider" />

                <!-- Timeline -->
                <section class="detail_section">
                    <h3>Timeline</h3>
                    <div class="timeline">
                        <div class="timeline_item">
                            <span class="timeline_label">Submitted:</span>
                            <span class="timeline_value">{{
                                formatDate(request.created_at)
                            }}</span>
                        </div>
                        <div class="timeline_item">
                            <span class="timeline_label">Last Updated:</span>
                            <span class="timeline_value">{{
                                formatDate(request.updated_at)
                            }}</span>
                        </div>
                        <div v-if="request.contacted_at" class="timeline_item">
                            <span class="timeline_label">Contacted:</span>
                            <span class="timeline_value">{{
                                formatDate(request.contacted_at)
                            }}</span>
                        </div>
                        <div
                            v-if="request.assigned_to_admin_id"
                            class="timeline_item"
                        >
                            <span class="timeline_label">Assigned To:</span>
                            <span class="timeline_value"
                                >Admin ID
                                {{ request.assigned_to_admin_id }}</span
                            >
                        </div>
                    </div>
                </section>

                <hr class="section_divider" />

                <!-- Action Buttons -->
                <section class="detail_section">
                    <div class="action_buttons">
                        <button @click="viewUserProfile" class="action_btn">
                            View User Profile
                        </button>
                        <button @click="openEmail" class="action_btn">
                            Send Email
                        </button>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<style scoped>
.modal_overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal_content {
    background: #ffffff;
    border-radius: 8px;
    width: 90%;
    max-width: 800px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid var(--border-color);
}

.modal_header h2 {
    margin: 0;
    font-size: 20px;
    color: var(--text-color);
}

.close_button {
    background: none;
    border: none;
    font-size: 24px;
    color: var(--text-color-secondary);
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
}

.close_button:hover {
    color: var(--text-color);
}

.modal_body {
    padding: 24px;
}

.loading_state {
    padding: 40px;
    text-align: center;
    color: var(--text-color-secondary);
}

.detail_section {
    margin-bottom: 24px;
}

.detail_section h3 {
    margin: 0 0 16px 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-color);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.section_divider {
    border: none;
    border-top: 1px solid var(--border-color);
    margin: 24px 0;
}

.status_controls {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.status_controls label {
    font-weight: 500;
    color: var(--text-color-secondary);
}

.status_buttons {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.status_btn {
    padding: 8px 16px;
    border: 2px solid transparent;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    text-transform: uppercase;
}

.status_btn.status_pending {
    background: #fff3cd;
    color: #856404;
}

.status_btn.status_contacted {
    background: #d1ecf1;
    color: #0c5460;
}

.status_btn.status_converted {
    background: #d4edda;
    color: #155724;
}

.status_btn.status_rejected {
    background: #f8d7da;
    color: #721c24;
}

.status_btn.active {
    border-color: var(--main-color);
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.2);
}

.info_grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 16px;
}

.info_item {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.info_item label {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-color-secondary);
}

.info_item span {
    font-size: 14px;
    color: var(--text-color);
}

.email_value {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: monospace;
}

.email_btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
    padding: 4px;
}

.licenses_value {
    font-weight: 600;
    color: var(--main-color);
}

.message_box {
    background: var(--background-secondary);
    padding: 16px;
    border-radius: 6px;
    border-left: 4px solid var(--main-color);
}

.message_box p {
    margin: 0;
    color: var(--text-color);
    line-height: 1.6;
}

.no_message {
    padding: 16px;
    text-align: center;
    color: var(--text-color-secondary);
    font-style: italic;
}

.notes_textarea {
    width: 100%;
    min-height: 120px;
    padding: 12px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--background-color);
    color: var(--text-color);
    font-family: inherit;
    font-size: 14px;
    resize: vertical;
    transition: border-color 0.2s ease;
}

.notes_textarea:focus {
    outline: none;
    border-color: var(--main-color);
}

.notes_textarea.warning {
    border-color: #ff9800;
}

.char_counter {
    font-size: 13px;
    color: var(--text-color-secondary);
}

.char_counter.warning {
    color: #ff9800;
    font-weight: 600;
}

.save_notes_btn {
    margin-top: 12px;
    padding: 8px 20px;
    background: var(--main-color);
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    transition: opacity 0.2s ease;
}

.save_notes_btn:hover:not(:disabled) {
    opacity: 0.9;
}

.save_notes_btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.timeline {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.timeline_item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: var(--background-secondary);
    border-radius: 4px;
}

.timeline_label {
    font-weight: 600;
    color: var(--text-color-secondary);
    font-size: 13px;
}

.timeline_value {
    font-family: monospace;
    font-size: 13px;
    color: var(--text-color);
}

.action_buttons {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.action_btn {
    padding: 10px 20px;
    background: var(--main-color);
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    transition: opacity 0.2s ease;
}

.action_btn:hover {
    opacity: 0.9;
}
</style>
